import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useToasts } from "app/pages/activation/hooks/helpers/useToasts";
import { translate } from "app/filters/i18nFilter";
import type { ShortTopicSchema, TopicSchema } from "app/pages/activation/components/Topics/types";
import { createActivationApi } from "app/pages/activation/services/api";

import type { BenchmarkGranularity } from "../../types/common";

import { TOPICS_QUERY_KEY } from "./useGetTopicsDataQuery";

const EDIT_TOPIC_MUTATION_KEY = "editTopic";

export const useUpdateTopicMutation = (activationId: string, granularity: BenchmarkGranularity) => {
    const { showErrorToast } = useToasts();
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: [EDIT_TOPIC_MUTATION_KEY],
        mutationFn: ({ activationId, topic }: { activationId: string; topic: ShortTopicSchema }) => {
            const api = createActivationApi();

            return api.topics.update({ activationId, topic });
        },
        onSuccess: ({ id }) => {
            const topicsList: TopicSchema[] = queryClient.getQueryData([TOPICS_QUERY_KEY, granularity, activationId]);

            const topicsModified = topicsList.map((topic) => {
                if (topic.topicId === id) {
                    topic.isPreloading = true;
                }

                return topic;
            });

            const newTopics = [...(topicsModified ?? [])];

            queryClient.setQueryData([TOPICS_QUERY_KEY, granularity, activationId], newTopics);
            queryClient.invalidateQueries({ queryKey: [TOPICS_QUERY_KEY, granularity, activationId] });
        },
        onError(error: Error) {
            showErrorToast(translate("common.api.error", { error: error?.message }));
        },
    });
};
